'use client'

import { ReactNode } from 'react'
import { Button, Container, Dialog, Divider, Navigation, Spacer, Text } from '@vinted/web-ui'

import { X24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  ReasonForNotBundling,
  cannotBundleHyperlinkClickEvent,
} from '@marketplace-web/bundles/items-bundle-data'

import { BUNDLES_POLICY_URL } from '../../constants/routes'

type Props = {
  show: boolean
  sellerId: number
  selectedItemId?: number
  bundleTrackingId: string | null
  onClose: () => void
}

const BundlingPolicyDialog = ({
  show,
  sellerId,
  selectedItemId,
  bundleTrackingId,
  onClose,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('items_multiselect')

  const trackingData = {
    listingId: selectedItemId ?? null,
    sellerId,
    reasonForNotBundling: ReasonForNotBundling.ShippingSize,
    screen: 'bundling',
    bundleSessionId: bundleTrackingId,
  }
  const values = {
    'faq-link': (chunks: Array<ReactNode>) => (
      <a
        key="faq-link"
        href={BUNDLES_POLICY_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="bundle-faq-link"
        onClick={() => track(cannotBundleHyperlinkClickEvent(trackingData))}
      >
        {chunks}
      </a>
    ),
  }

  return (
    <Dialog closeOnOverlay defaultCallback={onClose} show={show} testId="bundle-policy-dialog">
      <Navigation
        body={translate('bundling_policy_dialog.title')}
        right={
          <Button
            inverse
            inline
            iconName={X24}
            testId="bundle-policy-dialog-navigation-close-button"
            onClick={onClose}
          />
        }
        testId="bundle-policy-dialog-navigation"
      />
      <>
        <Divider />
        <Container>
          <Text type={Text.Type.Subtitle} as="span">
            {translate('bundling_policy_dialog.description')}
          </Text>
          <Spacer size={Spacer.Size.Large} />
          <Text type={Text.Type.Subtitle} as="span">
            {translate('bundling_policy_dialog.description_details')}
          </Text>
          <Spacer size={Spacer.Size.Medium} />
          <Text type={Text.Type.Subtitle} as="span" html>
            {translate('bundling_policy_dialog.policy_url', values)}
          </Text>
          <Spacer size={Spacer.Size.X2Large} />
          <Button
            styling={Button.Styling.Filled}
            text={translate('bundling_policy_dialog.dismiss')}
            onClick={onClose}
          />
        </Container>
      </>
    </Dialog>
  )
}

export default BundlingPolicyDialog
